package link.erpango.quip.utils

import androidx.compose.ui.graphics.ImageBitmap
import kotlinx.browser.document
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get
import kotlin.coroutines.resume

/**
 * Shared registry for blob URL -> filename mappings, accessible from JsPlatformManager
 */
object JsBlobRegistry {
    private val fileNameMap = mutableMapOf<String, String>()

    fun store(blobUrl: String, fileName: String) {
        fileNameMap[blobUrl] = fileName
    }

    fun getFileName(blobUrl: String): String {
        return fileNameMap[blobUrl] ?: "document"
    }
}

class JsFileUtils : IFileUtils {

    override fun log(message: String) {
        println(message)
    }

    override fun shareLogFile() {
        println("Share log file not supported in browser")
    }

    override suspend fun pickFile(allowedTypes: List<String>): FileData? = suspendCancellableCoroutine { cont ->
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        if (allowedTypes.isNotEmpty()) {
            input.accept = allowedTypes.joinToString(",")
        }

        input.onchange = { _ ->
            val file = input.files?.get(0)
            if (file != null) {
                val reader = FileReader()
                reader.onload = {
                    try {
                        val result = reader.result as? String ?: ""
                        val base64Content = if (result.contains(",")) {
                            result.substringAfter(",")
                        } else {
                            result
                        }
                        val mimeType = file.type.ifEmpty { "application/octet-stream" }
                        cont.resume(FileData(file.name, base64Content, mimeType))
                    } catch (e: Exception) {
                        println("Error reading file: ${e.message}")
                        cont.resume(null)
                    }
                    Unit
                }
                reader.onerror = {
                    println("FileReader error")
                    cont.resume(null)
                    Unit
                }
                reader.readAsDataURL(file)
            } else {
                cont.resume(null)
            }
            Unit
        }

        input.click()
    }

    override fun saveBase64ToFile(base64: String, fileName: String): String {
        try {
            // Detect MIME type from file extension
            val mimeType = when {
                fileName.endsWith(".pdf", ignoreCase = true) -> "application/pdf"
                fileName.endsWith(".png", ignoreCase = true) -> "image/png"
                fileName.endsWith(".jpg", ignoreCase = true) || fileName.endsWith(".jpeg", ignoreCase = true) -> "image/jpeg"
                fileName.endsWith(".gif", ignoreCase = true) -> "image/gif"
                fileName.endsWith(".webp", ignoreCase = true) -> "image/webp"
                else -> "application/octet-stream"
            }

            // Decode base64 to binary and create a Blob URL
            // Browsers block data: URLs in window.open(), but Blob URLs work
            val blobUrl = js("""
                (function(base64, mime) {
                    var byteString = atob(base64);
                    var ab = new ArrayBuffer(byteString.length);
                    var ia = new Uint8Array(ab);
                    for (var i = 0; i < byteString.length; i++) {
                        ia[i] = byteString.charCodeAt(i);
                    }
                    var blob = new Blob([ab], {type: mime});
                    return URL.createObjectURL(blob);
                })
            """) as (String, String) -> String

            val url = blobUrl(base64, mimeType)

            // Store the filename for later download use
            JsBlobRegistry.store(url, fileName)

            return url
        } catch (e: Exception) {
            println("Error saving base64 to file: ${e.message}")
            return ""
        }
    }

    override fun readFileToBase64(filePath: String): String {
        // Not applicable for blob URLs on JS
        return ""
    }

    override fun base64ToBitmap(base64: String): ImageBitmap? {
        return null
    }

    override fun fileExists(filePath: String): Boolean {
        return false
    }
}
